import React, { useState, useRef, useEffect , useCallback } from 'react';
import PTZPanel from './PTZPanel';

const GatewayCameraViewer = ({ camera, socket, defaultQuality = 'medium' }) => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const quality = defaultQuality;
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPortrait, setIsPortrait] = useState(false);

  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const frameCountRef = useRef(0);
  const containerRef = useRef(null);

  const displayVideoFrame = useCallback((base64Frame) => {
    if (!videoRef.current) return;
    if (!canvasRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = 640;
      canvas.height = 360;
      canvasRef.current = canvas;
      const stream = canvas.captureStream(30);
      videoRef.current.srcObject = stream;
    }

    const img = new Image();
    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas || !videoRef.current || !isStreamingRef.current) return;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      ctx.font = 'bold 14px Arial';
      const text = `🔴 LIVE ${camera.name}`;
      const x = 20;
      const y = 30;

      const metrics = ctx.measureText(text);
      const textWidth = metrics.width;
      const textHeight = 14;

      ctx.fillStyle = 'rgba(156, 156, 156, 0.39)';
      ctx.fillRect(x - 4, y - textHeight, textWidth + 8, textHeight + 6);

      ctx.fillStyle = 'white';
      ctx.fillText(text, x, y);
    };
    img.src = `data:image/jpeg;base64,${base64Frame}`;
  }, [camera.name]); // dependencias reales

  useEffect(() => {
    if (!socket) return;

    const handleFrame = (data) => {
      if (data.cameraId === camera.id && isStreaming) {
        displayVideoFrame(data.frame);
        frameCountRef.current++;
      }
    };

    const handleCameraStarted = (data) => {
      if (data.cameraId === camera.id) {
        setIsStreaming(true);
        setIsConnecting(false);
        setError(null);
      }
    };

    const handleCameraStopped = (data) => {
      if (data.cameraId === camera.id) {
        setIsStreaming(false);
        setIsConnecting(false);
        clearVideo();
      }
    };

    const handleCameraError = (data) => {
      if (data.cameraId === camera.id) {
        setError(data.error);
        setIsStreaming(false);
        setIsConnecting(false);
        clearVideo();
      }
    };


    socket.on('camera-frame', handleFrame);
    socket.on('camera-started', handleCameraStarted);
    socket.on('camera-stopped', handleCameraStopped);
    socket.on('camera-error', handleCameraError);

    return () => {
      socket.off('camera-frame', handleFrame);
      socket.off('camera-started', handleCameraStarted);
      socket.off('camera-stopped', handleCameraStopped);
      socket.off('camera-error', handleCameraError);
        clearVideo();
    };
  }, [socket, camera.id, isStreaming , displayVideoFrame]);

  const clearVideo = () => {
    if (videoRef.current) {
        videoRef.current.srcObject = null;
    }
    if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
    canvasRef.current = null; 
    frameCountRef.current = 0;
};

  const isStreamingRef = useRef(isStreaming);
const isConnectingRef = useRef(isConnecting);

useEffect(() => {
  isStreamingRef.current = isStreaming;
  isConnectingRef.current = isConnecting;
}, [isStreaming, isConnecting]);

    const startStream = useCallback(() => {
    if (!socket || isStreamingRef.current || isConnectingRef.current) return;
    setIsConnecting(true);
    setError(null);
    frameCountRef.current = 0;
    socket.emit('start-camera', { cameraId: camera.id, quality });
    }, [socket, camera.id, quality]);


  const stopStream = () => {
    if (!socket || !isStreaming) return;

    socket.emit('stop-camera', {
      cameraId: camera.id
    });

    setIsStreaming(false);
    clearVideo();
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      startStream();
    }, 1000);

    return () => clearTimeout(timer);
  }, [startStream]);

  const handleDoubleClick = () => {
    const container = containerRef.current;
    if (!container) return;

    if (document.fullscreenElement) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    } else {
      if (container.requestFullscreen) {
        container.requestFullscreen();
      } else if (container.webkitRequestFullscreen) {
        container.webkitRequestFullscreen();
      } else if (container.msRequestFullscreen) {
        container.msRequestFullscreen();
      }
    }
  };

  // Detectar cambios de fullscreen
  useEffect(() => {
  let lastFullscreen = null;
  let lastPortrait = null;

  const updateState = () => {
    const isFs = !!document.fullscreenElement;
    const isPortraitNow = window.matchMedia("(orientation: portrait)").matches;

    // Solo actualiza si cambió
    if (isFs !== lastFullscreen || isPortraitNow !== lastPortrait) {
      setIsFullscreen(isFs);
      setIsPortrait(isPortraitNow);
      console.log("🖥️ Fullscreen:", isFs, "📱 Portrait:", isPortraitNow);
      lastFullscreen = isFs;
      lastPortrait = isPortraitNow;
    }
  };

  const handleFsChange = () => {
    setTimeout(updateState, 250); // espera más para asegurar estabilidad
  };

  const handleResize = () => {
    if (document.fullscreenElement) {
      updateState();
    }
  };

  updateState(); // inicial
  document.addEventListener("fullscreenchange", handleFsChange);
  window.addEventListener("resize", handleResize);

  return () => {
    document.removeEventListener("fullscreenchange", handleFsChange);
    window.removeEventListener("resize", handleResize);
  };
}, []);



  return (
    <div className="camera-card gateway-camera">      
      <h3 className="camera-title">{camera.name}</h3>

      <div 
        className="video-container" 
        ref={containerRef} 
        onDoubleClick={handleDoubleClick}
      >
        <video
          ref={videoRef}
          className="video-player"
          autoPlay
          muted
          playsInline
        />

        {isFullscreen && isPortrait && (
          <div className="orientation-warning">
            📱 Por favor gira el dispositivo a horizontal
          </div>
        )}


        {isStreaming && isFullscreen && !isPortrait && (
          <div className="ptz-overlay">
            <PTZPanel cameraId={camera.id} />
          </div>
        )}
      </div>

      <div className="controls">
        {(isStreaming || isConnecting) ? (
          <button className="btn btn-danger" onClick={stopStream}>
            Detener
          </button>
        ) : (
          <button className="btn btn-success" onClick={startStream} disabled={isConnecting}>
            {isConnecting ? 'Conectando...' : 'Iniciar'}
          </button>
        )}
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
    </div>
  );
};

export default GatewayCameraViewer;
